import { useCallback, useState } from 'react'
import type { MutationResult, QueryResult } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export type CustomerioImportStatus = 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'

export interface CustomerioImportErrorEntry {
  at: string
  kind: 'fetch' | 'upsert' | 'rewrite' | 'unmapped'
  message: string
  source?: { kind: string; id?: string; name?: string }
}

export interface CustomerioImport {
  id: string
  status: CustomerioImportStatus
  phase: string | null
  counts: Record<string, number>
  errors: CustomerioImportErrorEntry[]
  startedAt: string
  completedAt: string | null
}

/** Poll every 3s while any import is RUNNING; stop once none are. */
const runningImportsPollInterval = (data: CustomerioImport[] | undefined) =>
  data?.some((item) => item.status === 'RUNNING') ? 3000 : false

export function useCustomerioImports(): QueryResult<CustomerioImport[]> {
  // TanStack keeps existing data visible during the poll refetches, so the
  // table doesn't collapse into a skeleton every tick — the same
  // stale-while-revalidate behavior the old hand-rolled hasDataRef gave us.
  return useApiQuery<CustomerioImport[]>('/api/customerio-imports', {
    refetchInterval: runningImportsPollInterval,
  })
}

export function useCustomerioImport(id: string | null): QueryResult<CustomerioImport> {
  return useApiQuery<CustomerioImport>(id ? `/api/customerio-imports/${id}` : null)
}

export function useStartCustomerioImport(): MutationResult<{ importId: string }, []> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(async () => {
    setLoading(true)
    try {
      return await fetchApi<{ importId: string }>('/api/customerio-imports', {
        method: 'POST',
        body: JSON.stringify({}),
      })
    } finally {
      setLoading(false)
    }
  }, [fetchApi])
  return { perform, loading }
}

export function useCancelCustomerioImport(): MutationResult<CustomerioImport, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (importId: string) => {
      setLoading(true)
      try {
        return await fetchApi<CustomerioImport>(`/api/customerio-imports/${importId}/cancel`, {
          method: 'POST',
          body: JSON.stringify({}),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
